import React, { useState, useCallback } from 'react'
import { Map as ImmutableMap } from 'immutable'
import { Box, Typography, Alert, TextField, IconButton, Tooltip } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import MuiButton from 'gui/components/common/MuiButton'
import { useTranscriptFetcher } from './ai-helpers'

const COPY_SUCCESS_DURATION_MS = 2000

interface AITranscriptProps {
  /** Immutable Map containing content data (must include contentId) */
  content: ImmutableMap<string, unknown>
  /** Whether the form is in view-only mode */
  isViewOnlyMode: boolean
  /** ID of the attached media (used to disable when no feature media) */
  featureMediaId?: number | string | null
}

/**
 * Fetch transcript only and show it in a text field with a copy button.
 * Does not call any AI endpoints—just transcript fetch + copy.
 */
function AITranscript ({ content, isViewOnlyMode, featureMediaId }: AITranscriptProps) {
  const {
    fetchTranscript,
    transcript,
    loading: transcriptLoading,
    error: transcriptError,
  } = useTranscriptFetcher()

  const [copySuccess, setCopySuccess] = useState(false)

  const contentId = content.get('contentId') as number | string | undefined

  const handleFetchTranscript = useCallback((): void => {
    if (!contentId) return
    fetchTranscript(contentId)
  }, [contentId, fetchTranscript])

  const handleRetry = useCallback((): void => {
    if (!contentId) return
    fetchTranscript(contentId)
  }, [contentId, fetchTranscript])

  const handleCopyToClipboard = useCallback(async (): Promise<void> => {
    if (!transcript) return
    try {
      await navigator.clipboard.writeText(transcript)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), COPY_SUCCESS_DURATION_MS)
    } catch {
      // Clipboard can fail (e.g. permissions); no user feedback for now
    }
  }, [transcript])

  const isButtonDisabled = !featureMediaId || isViewOnlyMode || transcriptLoading || !contentId

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6" gutterBottom>
        Transcript
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Fetch the video transcript and copy it to your clipboard. No AI processing—just the raw
        transcript.
      </Typography>

      {transcriptError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <MuiButton color="primary" size="small" onClick={handleRetry}>
              Retry
            </MuiButton>
          }
        >
          {transcriptError}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <MuiButton
          variant="contained"
          color="primary"
          onClick={handleFetchTranscript}
          disabled={isButtonDisabled}
        >
          {transcriptLoading ? 'Fetching transcript…' : 'Fetch transcript'}
        </MuiButton>
      </Box>

      {transcript && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Transcript ({transcript.length.toLocaleString()} characters)
            </Typography>
            <Tooltip title={copySuccess ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton
                onClick={handleCopyToClipboard}
                color="primary"
                size="small"
                aria-label="Copy transcript to clipboard"
              >
                {copySuccess ? <CheckIcon /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={8}
            maxRows={20}
            value={transcript}
            variant="outlined"
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: 'monospace',
                fontSize: '0.85rem',
              },
            }}
            sx={{
              '& .MuiInputBase-root': {
                backgroundColor: '#fff',
              },
            }}
          />
        </Box>
      )}
    </Box>
  )
}

export default AITranscript
